import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { isEmpty } from '@carecloud/cloudpak';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './selectionCard.module.scss';
import { store } from '../../../models';
import { ContextProvider } from '../../root/TopLevelContext';
import * as babyContexts from '../../../contexts';

// Generic single-select "card" row (icon + title + description, with a
// selected/unselected visual state). Not tied to modals — usable anywhere a
// schema tree is rendered.
//
// Cards that share a `groupName` are mutually exclusive: clicking one stores
// its `value` under that groupName in the existing radioInput redux slice
// (the same store RadioInput already uses for "which of these is selected"),
// so any other card in the group re-renders as unselected. Without a
// groupName, a card just falls back to the static `selected` prop and only
// fires its own `action` on click, same as before.
const selectionCard = React.memo(({
  id,
  icon,
  title,
  description,
  selected: initialSelected,
  groupName,
  value,
  fullValue,
  action = {},
  theme,
  radioInput,
}) => {
  const { model = String(), trigger = String(), param = String(), babyContext = String() } = action;
  const grandpaContext = useContext(ContextProvider);
  const variableContext = babyContext && useContext(babyContexts[`${babyContext}Context`]);
  const cardValue = value || id;
  const selected = groupName ? radioInput[groupName] === cardValue : initialSelected;
  const clickable = Boolean(groupName) || !isEmpty(action);

  // fullValue is the unmasked counterpart to a redacted `description` (see
  // SelectionCard.ts) — stored under its own key alongside `value` so
  // anything needing the real destination (e.g. sendFindAccountCode) doesn't
  // have to scrape it back out of the rendered, masked text.
  useEffect(() => {
    if (groupName && initialSelected && radioInput[groupName] === undefined) {
      store.dispatch.radioInput.updateValue(groupName, cardValue);
      store.dispatch.radioInput.updateValueId(`${groupName}Value`, id);
      if (fullValue) store.dispatch.radioInput.updateValue(`${groupName}FullValue`, fullValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clickHandler = () => {
    if (groupName) {
      store.dispatch.radioInput.updateValue(groupName, cardValue);
      store.dispatch.radioInput.updateValueId(`${groupName}Value`, id);
      if (fullValue) store.dispatch.radioInput.updateValue(`${groupName}FullValue`, fullValue);
    }
    if (!isEmpty(action)) {
      if (model === 'localActions') grandpaContext._getAction(trigger) && grandpaContext._getAction(trigger)(param);
      else if (babyContext) variableContext[trigger](param);
      else store.dispatch[model][trigger](param);
    }
  };

  return (
    <div
      id={id}
      className={classNames(styles.selectionCard, { [styles.selected]: selected }, styles[theme])}
      {...(clickable && { role: 'button', tabIndex: 0, onClick: clickHandler })}
    >
      <div className={styles.icon}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className={styles.text}>
        <h5 className={styles.title}>{title}</h5>
        <h6 className={styles.description}>{description}</h6>
      </div>
      {selected && <FontAwesomeIcon icon="check" className={styles.check} />}
    </div>
  );
});

const mapStateToProps = ({ radioInput }) => ({ radioInput });

export const SelectionCard = connect(mapStateToProps)(selectionCard);
